import { useEffect, useState } from "react";

/**
 * Text input that displays whole numbers with Indian (en-IN) grouping — 750000 → 7,50,000 —
 * while reporting the raw numeric value via onChange(number|null). Digits-only entry.
 *
 * Props: value (number|null|""), onChange(num), allowNull (empty → null vs 0),
 * zeroAsEmpty (render 0 as blank, default true), plus any passthrough input props
 * (className, disabled, placeholder, data-testid, min, max, id, …).
 */
export default function GroupedNumberInput({
  value,
  onChange,
  allowNull = false,
  zeroAsEmpty = true,
  ...rest
}) {
  const format = (v) => {
    if (v === "" || v === null || v === undefined) return "";
    const num = Number(v);
    if (!Number.isFinite(num)) return "";
    if (num === 0 && zeroAsEmpty) return "";
    return num.toLocaleString("en-IN");
  };

  const [display, setDisplay] = useState(format(value));

  // Reformat only when the external value differs from what's shown (form load / reset /
  // rejected edit) — not on our own keystrokes, so typing isn't disrupted.
  useEffect(() => {
    const rawDigits = display.replace(/,/g, "");
    const curNum = rawDigits === "" ? null : Number(rawDigits);
    const extNum = value === "" || value === null || value === undefined ? null : Number(value);
    if (curNum !== extNum) setDisplay(format(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, "");
    if (digits === "") {
      setDisplay("");
      onChange(allowNull ? null : 0);
      return;
    }
    const num = parseInt(digits, 10);
    setDisplay(num.toLocaleString("en-IN"));
    onChange(num);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={display}
      onChange={handleChange}
      {...rest}
    />
  );
}
